"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

export type Theme = "light" | "dark" | "system";

interface Ctx {
  theme: Theme;
  resolved: "light" | "dark";
  setTheme: (t: Theme) => void;
  cycle: () => void;
}

const ThemeCtx = createContext<Ctx | null>(null);
const STORAGE_KEY = "bell-theme";

function systemDark() {
  return typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// Inline script string — runs before paint to prevent theme flash (FOUC).
export const themeScript = `(function(){try{var t=localStorage.getItem("${STORAGE_KEY}");if(t&&t!=="system"){document.documentElement.setAttribute("data-theme",t);}}catch(e){}})();`;

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // null until storage is read — the apply effect must not touch <html>
  // before then, or it wipes what the pre-paint inline script set and the
  // page flashes the system theme for one frame.
  const [theme, setThemeState] = useState<Theme | null>(null);
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  // Hydrate from storage once mounted.
  useEffect(() => {
    const saved = (localStorage.getItem(STORAGE_KEY) as Theme) || "system";
    setThemeState(saved);
  }, []);

  // Apply to <html> + track resolved value.
  useEffect(() => {
    if (theme === null) return;
    const root = document.documentElement;
    const applyResolved = () => {
      const dark = theme === "dark" || (theme === "system" && systemDark());
      setResolved(dark ? "dark" : "light");
    };

    if (theme === "system") {
      root.removeAttribute("data-theme");
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      applyResolved();
      mq.addEventListener("change", applyResolved);
      return () => mq.removeEventListener("change", applyResolved);
    }

    root.setAttribute("data-theme", theme);
    applyResolved();
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
  }, []);

  const cycle = useCallback(() => {
    setTheme(resolved === "dark" ? "light" : "dark");
  }, [resolved, setTheme]);

  return (
    <ThemeCtx.Provider value={{ theme: theme ?? "system", resolved, setTheme, cycle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
